import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { UserContext } from '../../../../context';
import { useOrganisationRelatedStoriesQuery } from '../../../../services';
import { Button, ButtonType, GalleryItem, Switch } from '../../../../shared';
import { StorySyncRow } from './StorySyncRow';

import classes from './StorySync.module.scss';

export interface StorySyncSource {
  channelId?: number;
  storyId?: number;
  fetchLastCards: number;
}

export interface StorySyncState {
  enabled: boolean;
  sources: StorySyncSource[];
}

interface SourceChangeArgs {
  index: number;
  source: StorySyncSource;
}

interface StorySyncProps {
  storySync: StorySyncState;
  setStorySync: Dispatch<SetStateAction<StorySyncState>>;
  setTitle: (title: string) => void;
  setSubtitle: (subTitle: string) => void;
  setGalleryItems: (gallery: GalleryItem[]) => void;
  storyId?: number;
  withToggle?: boolean;
}

export const StorySync: FunctionComponent<StorySyncProps> = memo(
  ({ storySync, setStorySync, setTitle, setSubtitle, setGalleryItems, storyId, withToggle }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'createStorySettings' });

    const { channelId } = useContext(UserContext).userInfo.userData;

    const { data: channels, isFetching } = useOrganisationRelatedStoriesQuery({ channelId });

    const { enabled, sources } = storySync;

    const editMode = useMemo(() => Boolean(storyId), [storyId]);

    const channelItems = useMemo(
      () =>
        channels?.map(({ id, name }) => {
          return { id, title: name };
        }) ?? [],
      [channels]
    );

    const linkedSources = useMemo(
      () => sources.filter(({ storyId: sourceStoryId }) => Boolean(sourceStoryId)),
      [sources]
    );

    const toggleEnabled = useCallback(
      (enabled: boolean) => setStorySync((storySyncPrev) => ({ ...storySyncPrev, enabled })),
      [setStorySync]
    );

    const onSourceChange = useCallback(
      ({ index, source }: SourceChangeArgs) =>
        setStorySync((storySyncPrev) => ({
          ...storySyncPrev,
          sources: storySyncPrev.sources.map((item, itemIndex) =>
            itemIndex === index ? source : item
          ),
        })),
      [setStorySync]
    );

    const onSourceRemove = useCallback(
      (index: number) =>
        setStorySync((storySyncPrev) => ({
          ...storySyncPrev,
          sources: storySyncPrev.sources.filter((_, itemIndex) => itemIndex !== index),
        })),
      [setStorySync]
    );

    const onSourceAdd = useCallback(
      () =>
        setStorySync((storySyncPrev) => ({
          ...storySyncPrev,
          sources: [...storySyncPrev.sources, { fetchLastCards: 0 }],
        })),
      [setStorySync]
    );

    const copyFromSource = useCallback(() => {
      const linkedSource = linkedSources[0];

      if (!linkedSource) {
        return;
      }

      const story = channels
        ?.find(({ id }) => id === linkedSource.channelId)
        ?.stories.find(({ id }) => id === linkedSource.storyId);

      if (!story) {
        return;
      }

      const { title, subtitle, image } = story;

      setTitle(title);
      setSubtitle(subtitle);
      setGalleryItems(image ? [{ image }] : []);
    }, [channels, linkedSources, setGalleryItems, setSubtitle, setTitle]);

    const rows = useMemo(
      () =>
        sources.map((source, index) => {
          const otherStoryIds = new Set(
            sources.filter((_, otherIndex) => otherIndex !== index).map(({ storyId }) => storyId)
          );

          const stories = channels?.find(({ id }) => id === source.channelId)?.stories ?? [];

          const storyItems = stories
            .filter(({ id }) => id !== storyId)
            .map(({ id, title }) => ({
              id,
              title,
              ...(otherStoryIds.has(id) && {
                disabled: true,
                hint: ctxT('story-sync-mix-already-linked'),
              }),
            }));

          return (
            <StorySyncRow
              key={index}
              source={source}
              channelItems={channelItems}
              storyItems={storyItems}
              disabled={!enabled}
              withFetchLastItems={!editMode}
              onChange={(source) => onSourceChange({ index, source })}
              onRemove={() => onSourceRemove(index)}
            />
          );
        }),
      [
        channelItems,
        channels,
        ctxT,
        editMode,
        enabled,
        onSourceChange,
        onSourceRemove,
        sources,
        storyId,
      ]
    );

    const content = useMemo(() => {
      if (isFetching) {
        return <Skeleton height={'5rem'} />;
      }

      return (
        <>
          {withToggle && (
            <Switch
              label={ctxT('story-sync-switch-label')}
              checked={enabled}
              onChange={toggleEnabled}
            />
          )}

          {rows}

          {!sources.length && (
            <div className={classes['story-sync__hint']}>{ctxT('story-sync-empty')}</div>
          )}

          <Button
            type={ButtonType.secondary}
            label={ctxT('story-sync-add-source')}
            className={classes['story-sync__add']}
            onClick={onSourceAdd}
            disabled={!enabled || sources.some(({ storyId }) => !storyId)}
          />

          {sources.length === 1 && linkedSources.length === 1 && (
            <Button
              type={ButtonType.secondary}
              label={t('createStory.copy-from-source-story')}
              className={classes['story-sync__copy-from-source']}
              onClick={copyFromSource}
              disabled={!enabled}
            />
          )}
        </>
      );
    }, [
      copyFromSource,
      ctxT,
      enabled,
      isFetching,
      linkedSources.length,
      onSourceAdd,
      rows,
      sources,
      t,
      toggleEnabled,
      withToggle,
    ]);

    return <div className={classes['story-sync']}>{content}</div>;
  }
);
